import React from 'react'
import Head from 'next/head'
import Link from 'next/link'
import Header from '../components/Header'
import Footer from '../components/Footer'
import CommonHeadTags from '../components/CommonHeadTags'
import { GoogleAnalyticsHead } from '../components/GoogleAnalytics'
import ShadowCard from '../components/ShadowCard'
import withLocale from '../translations/withLocale'
import style from '../components/PrivPolicy.module.scss'

const PrivacyPolicy = () => {
  return (
    <>
      <Head>
        <title>Privacy Policy | Guitar-Tuner.org</title>
        <meta
          name='description'
          content='Learn how Guitar-Tuner.org handles microphone access, cookies, analytics, advertising data, server logs, and privacy requests.'
        />
        <meta httpEquiv='content-language' content='en' />
        <link rel='canonical' href='https://guitar-tuner.org/privacy-policy' />
        <link
          rel='alternate'
          hrefLang='x-default'
          href='https://guitar-tuner.org/privacy-policy'
        />
        <link
          rel='alternate'
          hrefLang='en'
          href='https://guitar-tuner.org/privacy-policy'
        />
        <CommonHeadTags
          title='Privacy Policy | Guitar-Tuner.org'
          description='Learn how Guitar-Tuner.org and ChordChord OÜ handle microphone audio, cookies, analytics, advertising data, and privacy requests.'
          url='https://guitar-tuner.org/privacy-policy'
        />
        <GoogleAnalyticsHead enableTracking={false} />
      </Head>

      <Header />

      <main className={style.privPolicy}>
        <ShadowCard>
          <div className={style.textContainer}>
            <h1>Privacy Policy</h1>
            <p><strong>Last updated:</strong> August 15, 2026</p>

            <p>
              This Privacy Policy explains how Guitar-Tuner.org collects and
              uses information when you visit the website or use the online
              tuner. It also explains the choices and rights available to you.
            </p>
            <p>
              If you use our Guitar Tuner app for iPhone or iPad, see the
              separate{' '}
              <Link href='/ios-privacy'><a>iOS App Privacy Policy</a></Link>.
            </p>

            <h2>Who is responsible for your data?</h2>
            <p>
              The data controller for Guitar-Tuner.org is:
            </p>
            <p>
              <strong>ChordChord OÜ</strong><br />
              Sepapaja 6<br />
              Tallinn 15551<br />
              Estonia<br />
              Email: <a href='mailto:team@guitar-tuner.org'>team@guitar-tuner.org</a>
            </p>

            <h2>Information we process</h2>

            <h3>Microphone access and audio</h3>
            <p>
              If you choose automatic tuning, your browser asks for permission
              to access your microphone. The tuner analyses the audio signal
              locally in your browser to estimate pitch. Guitar-Tuner.org does
              not record, upload, or store the microphone audio used by the
              tuner. You can revoke microphone permission at any time in your
              browser or device settings.
            </p>

            <h3>Server and technical logs</h3>
            <p>
              When you request a page, our hosting infrastructure may process
              technical information such as your IP address, browser and device
              type, requested URL, referring URL, and the date and time of the
              request. We use this information to deliver the website, maintain
              security, diagnose errors, and understand service performance.
            </p>

            <h3>Referral information</h3>
            <p>
              The website uses a first-party session cookie named{' '}
              <code>came_from</code>. It stores the referring page reported by
              your browser, or the word “direct” when no referrer is available.
              We use it to understand how visitors find the website. Because it
              is a session cookie, it normally expires when you close your
              browser.
            </p>

            <h3>Analytics and tag management</h3>
            <p>
              We use Google Tag Manager to manage website measurement tags.
              Tags configured through it may collect information about page
              views, interactions, browser and device characteristics, and
              approximate location derived from an IP address. Google may use
              cookies or similar technologies when these services are active.
              Learn more about how Google processes information from partner
              sites in{' '}
              <a href='https://policies.google.com/technologies/partner-sites'>
                Google’s information for partner sites
              </a>.
            </p>

            <h3>Hotjar</h3>
            <p>
              With your consent, we use Hotjar to understand how visitors use
              the website, for example through heatmaps, interaction data, and
              session recordings. Hotjar may process device and browser
              information, IP-derived location, pages visited, and interactions
              with the website. We do not intend to send microphone audio to
              Hotjar. Hotjar remains disabled until you accept optional
              services. See the{' '}
              <a href='https://www.hotjar.com/legal/policies/privacy/'>
                Hotjar Privacy Policy
              </a>.
            </p>

            <h3>Meta Pixel</h3>
            <p>
              With your consent, we use the Meta Pixel to measure the results
              of advertising, understand visits that follow Meta ads, and build
              audiences for advertising where permitted. Meta may process page
              visits, browser and device information, IP address, referring
              information, and identifiers stored by Meta technologies. Meta
              Pixel remains disabled until you accept optional services. See
              the{' '}
              <a href='https://www.facebook.com/privacy/policy/'>
                Meta Privacy Policy
              </a>.
            </p>

            <h3>Error monitoring</h3>
            <p>
              We may use Sentry to identify crashes and technical errors. Error
              reports can include diagnostic information such as the affected
              page, browser and device details, timestamps, IP address, and
              technical events leading up to an error. We use this information
              to keep the tuner reliable and secure. See the{' '}
              <a href='https://sentry.io/privacy/'>Sentry Privacy Policy</a> for
              more information.
            </p>

            <h3>Advertising</h3>
            <p>
              We use or intend to use Google AdSense to display advertising.
              Google and its advertising partners may use cookies, device
              identifiers, IP addresses, and information about visits to this
              and other websites to deliver, measure, limit, and personalise
              ads, depending on your region and consent choices. Google also
              uses this information to detect fraud and abuse.
            </p>
            <p>
              You can learn more in{' '}
              <a href='https://policies.google.com/technologies/ads'>
                Google’s advertising privacy information
              </a>{' '}
              and manage personalised advertising through{' '}
              <a href='https://myadcenter.google.com/'>Google My Ad Center</a>.
            </p>

            <h3>Contact messages</h3>
            <p>
              If you contact us by email, we process your email address, the
              contents of your message, and any information you choose to
              provide so that we can respond and keep an appropriate record of
              the request.
            </p>

            <h2>Why we process information</h2>
            <p>Depending on the context, we process information to:</p>
            <ul>
              <li>provide the website and tuner you request;</li>
              <li>protect the website and prevent misuse;</li>
              <li>diagnose errors and improve performance;</li>
              <li>understand how the website is used;</li>
              <li>respond to messages and privacy requests;</li>
              <li>display and measure advertising; and</li>
              <li>comply with legal obligations.</li>
            </ul>
            <p>
              Our legal bases under the GDPR may include performance of a
              service you request, our legitimate interests in operating and
              improving the website, your consent where required for
              non-essential cookies or personalised advertising, and compliance
              with legal obligations. You may withdraw consent at any time
              without affecting processing that took place before withdrawal.
            </p>

            <h2>Cookies and your choices</h2>
            <p>
              In the European Economic Area, United Kingdom, Switzerland, and
              other regions where prior consent is required, we use Google's
              certified consent management platform, integrated with the IAB
              Transparency and Consent Framework, to present available choices
              and communicate them to participating services. Google stores the
              resulting consent signal so the selection can be respected on
              later visits.
            </p>
            <p>
              Outside these consent regions, optional services may start
              automatically where permitted by applicable law. You can still
              opt out at any time through the “Privacy choices” link in the
              footer. We use coarse country information supplied by our delivery
              infrastructure to apply the relevant regional defaults. We do not
              use precise location information for this purpose.
            </p>
            <p>
              You can revisit or withdraw your selection through the “Privacy
              choices” link in the footer. When consent is required, Hotjar and
              Meta Pixel are not loaded through Google Tag Manager unless the
              relevant analytics and advertising purposes have been granted.
              You can also delete or block cookies using your browser settings.
              Rejecting optional services does not prevent you from using the
              tuner.
            </p>

            <h2>Who receives information?</h2>
            <p>
              We may share information with service providers that help us host,
              secure, measure, troubleshoot, and monetise the website, including
              hosting providers, Google, Sentry, Hotjar Ltd, and Meta Platforms
              Ireland Limited. These providers process information under their
              own terms or on our behalf. We may also
              disclose information when required by law, to protect legal
              rights, or in connection with a corporate transaction.
            </p>

            <h2>International transfers</h2>
            <p>
              Some service providers may process information outside Estonia
              or the European Economic Area. Where required, transfers are
              protected using an adequacy decision, standard contractual
              clauses, or another lawful transfer mechanism.
            </p>

            <h2>How long do we keep information?</h2>
            <p>
              We keep information only for as long as reasonably necessary for
              the purposes described above, including security, accounting, and
              legal requirements. Retention periods vary by data type and
              service provider. The <code>came_from</code> cookie normally lasts
              for the browser session. Contact correspondence is retained only
              while it is needed to respond, maintain appropriate records, or
              resolve a dispute.
            </p>

            <h2>Your data protection rights</h2>
            <p>
              Depending on your location and applicable law, you may have the
              right to request access to, correction of, deletion of, or a copy
              of your personal data; to restrict or object to processing; and
              to withdraw consent. You may also have the right to lodge a
              complaint with a data protection authority.
            </p>
            <p>
              To exercise your rights, email{' '}
              <a href='mailto:team@guitar-tuner.org'>team@guitar-tuner.org</a>.
              We may need to verify your identity before completing a request.
              In Estonia, the supervisory authority is the{' '}
              <a href='https://www.aki.ee/en'>Estonian Data Protection Inspectorate</a>.
            </p>

            <h2>Children’s privacy</h2>
            <p>
              Guitar-Tuner.org is a general-audience music tool and is not
              directed specifically to children. We do not knowingly ask
              children to provide personal information. If you believe a child
              has provided personal information to us, contact us so we can
              review and delete it where appropriate.
            </p>

            <h2>Changes to this policy</h2>
            <p>
              We may update this Privacy Policy when the website, our providers,
              or legal requirements change. We will publish the revised version
              on this page and update the date at the top.
            </p>
          </div>
        </ShadowCard>
      </main>

      <Footer />
    </>
  )
}

export default withLocale(PrivacyPolicy)
